import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DefaultImageDirective } from '@shared/directives';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { SurveysComposition } from '@shared/compositions';
import { SurveyEntity } from '@core/entities';
import { DialogRef } from '@ngneat/dialog';
import { GetUserId, isMobileView } from '@core/utils';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SurveyOptions, SurveyPollType, SurveyStatus, SurveyType, SurveyViewOption } from '@core/enums';
import { iter } from 'rrule/dist/esm/iter';
import { TruncateTextPipe } from '@shared/pipes';
import { AlertMessageComponent, ButtonComponent, IconComponent } from '@shared/components/ui';
import { SurveyFormComponent } from '@shared/components/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-survey-detail',
  imports: [CommonModule, DefaultImageDirective, IconComponent, NgxSkeletonLoaderModule, ReactiveFormsModule, TranslateModule, ButtonComponent, TruncateTextPipe, AlertMessageComponent],
  templateUrl: './survey-detail.component.html',
  styleUrls: ['./survey-detail.component.scss'],
})
export class SurveyDetailComponent extends SurveysComposition implements OnInit {
  selectedSurvey: SurveyEntity;
  bottomSheetData: any = {};
  currentIndex = 0;
  pollType: SurveyPollType;

  protected readonly isMobileView = isMobileView;
  protected readonly SurveyOptions = SurveyOptions;
  protected readonly SurveyStatus = SurveyStatus;
  protected readonly SurveyType = SurveyType;
  protected readonly iter = iter;
  protected readonly SurveyViewOption = SurveyViewOption;
  protected readonly SurveyPollType = SurveyPollType;
  private readonly _dialogRef: DialogRef;

  private _surveyId: number; // for surveyId passed form query params of parent component

  constructor() {
    super();
    if (!isMobileView()) {
      this._dialogRef = inject(DialogRef);
    }
  }

  get isFirstRecord(): boolean {
    return this.currentIndex === 0;
  }

  get isLastRecord(): boolean {
    return this.currentIndex === this.surveys.length - 1;
  }

  get isUserOwner(): boolean {
    return this.currentSurvey?.owner.id === +GetUserId();
  }

  get canEditDelete() {
    if (+this.currentSurvey.status === SurveyStatus.Close || new Date(this.currentSurvey.endDate) < new Date()) {
      return false;
    }
    return this._permissionService.isAdmin() || this.isUserOwner;
  }

  ngOnInit() {
    if (isMobileView()) {
      this.currentSurvey = this._bottomSheet.currentSheetData.survey || new SurveyEntity();
      this.surveys = this._bottomSheet.currentSheetData?.surveys || [];
      this.pollType = this._bottomSheet?.currentSheetData.surveyPollType;
      this._surveyId = this._bottomSheet.currentSheetData?.surveyId;
    } else {
      this.currentSurvey = this._dialogRef?.data.survey || new SurveyEntity();
      this.pollType = this._dialogRef?.data.surveyPollType;
      this.surveys = this._dialogRef.data.surveys || [];
      this._surveyId = this._dialogRef?.data.surveyId;
      this.currentIndex = this.surveys.findIndex((survey) => survey.id === this.currentSurvey.id);
      this.selectedSurvey = this.surveys[this.currentIndex];
    }
    if ((this.currentSurvey && this.currentSurvey.id) || this._surveyId) {
      this._fetchSurveyById(this.currentSurvey.id || this._surveyId, false);

      // this._socketService
      //   .fromEvent(SocketEvents.GetNotification)
      //   .pipe(untilDestroyed(this))
      //   .subscribe((data: NotificationType) => {
      //     if (data === NotificationType.Survey) {
      //       this.isLoading = true;
      //       this.surveyLoaders.isResultLoading = true;
      //       this.surveyLoaders.isFormLoading = true;
      //       this._fetchSurveyById(this.currentSurvey.id, false);
      //       this._fetchAllSurveyResultById();
      //     }
      //   });
    }
    this._initSurveyVoteForm();
  }

  closeDialog() {
    if (isMobileView()) {
      this._bottomSheet.close();
    } else {
      this._dialogRef.close();
    }
  }

  onRadioChange(e: Event) {
    const target = e.target as HTMLInputElement;
    if (target.checked) {
      this.form.get('surveyAnswerId').setValue([target.value]); // Store as an array
    }
  }

  onCheckboxChange(id: number, isChecked: boolean) {
    const surveyAnswerIds = this.form.get('surveyAnswerId').value || [];
    if (isChecked) {
      // Add ID to array if checked
      this.form.get('surveyAnswerId').setValue([...surveyAnswerIds, id]);
    } else {
      // Remove ID from array if unchecked
      this.form.get('surveyAnswerId').setValue(surveyAnswerIds.filter((x: number) => x !== id));
    }
  }

  checkMyVote() {
    const results = this.currentSurvey?.surveyResults?.filter((item) => item.userId === +GetUserId()) || [];
    const myVote = results.length > 0;
    return !myVote;
  }

  showVote(vote: any) {
    if (vote.surveyViewOption === SurveyViewOption.Always) {
      if (this._permissionService.isAdmin()) {
        return true;
      } else {
        return true;
      }
    } else if (vote.surveyViewOption === SurveyViewOption.Never) {
      return this._permissionService.isAdmin();
    } else if (vote.surveyViewOption === SurveyViewOption.AfterVoting) {
      if (this._permissionService.isAdmin()) {
        return true;
      } else {
        return this.currentSurvey.surveyAnswer && this.currentSurvey.surveyAnswer.length > 0;
      }
    }
    return true;
  }

  editSurveyDialog() {
    this._dialogService
      .open(SurveyFormComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset dialog-overflow-y-auto',
        data: {
          id: this.currentSurvey.id,
        },
      })
      .afterClosed$.pipe(untilDestroyed(this))
      .subscribe({
        next: (data) => {
          if (data) {
            this._dialogService.closeAll();
          }
        },
      });
  }

  loadPrevSurvey() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.currentSurvey = this.surveys[this.currentIndex];
    } else {
      console.log('No previous Survey available');
    }
  }

  loadNextSurvey() {
    if (this.currentIndex < this.surveys.length - 1) {
      this.currentIndex++;
      this.currentSurvey = this.surveys[this.currentIndex];
    } else {
      console.log('No next Survey available');
    }
  }

  editSurveyBottomSheet(survey: SurveyEntity) {
    this._bottomSheet.open(SurveyFormComponent, {
      height: '95%',
      showCloseButton: false,
      data: {
        id: survey.id,
      },
    });
  }

  openVoteResultDialogBox(surveyId: number, answerId: number) {
    this._showVotingByResults(surveyId, answerId);
  }
}
